/**
 * BottomNav - Navegação inferior mobile
 *
 * Componente de navegação estilo app nativo para dispositivos móveis.
 * Exibe os principais itens de navegação na parte inferior da tela.
 *
 * @example
 * <BottomNav role="investor" />
 */

import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext.hook';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Home,
  LayoutDashboard,
  PieChart,
  Briefcase,
  TrendingUp,
  Users,
  Plus,
  X,
  Calculator,
  UserPlus,
  FolderPlus,
  CircleDollarSign,
  Banknote,
  HelpCircle,
  Settings,
  LogOut,
  Award,
} from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export type BottomNavRole = 'admin' | 'partner' | 'assessor' | 'investor' | 'incorporador';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface BottomNavProps {
  /** Role do usuário para determinar itens de navegação */
  role: BottomNavRole;
  /** Classes adicionais */
  className?: string;
}

// =============================================================================
// NAVIGATION CONFIGS
// =============================================================================

const getNavigationItems = (role: BottomNavRole, basePath?: string): NavItem[] => {
  switch (role) {
    case 'admin':
      return [
        { label: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
        { label: 'Investimentos', href: '/admin/investments', icon: TrendingUp },
        // O botão + é renderizado separadamente no centro
        { label: 'Usuários', href: '/admin/users', icon: Users },
      ];

    case 'partner': {
      const isMFBorMFA = basePath === '/mfb' || basePath === '/mfa';
      return [
        { label: 'Dashboard', href: `${basePath}/dashboard`, icon: LayoutDashboard },
        { label: 'Carteira', href: `${basePath}/carteira`, icon: PieChart },
        ...(isMFBorMFA ? [{ label: 'Ativos', href: `${basePath}/ativos`, icon: TrendingUp }] : []),
        { label: 'Produtos', href: '/products', icon: Briefcase },
      ];
    }

    case 'assessor':
      return [
        { label: 'Dashboard', href: '/assessor/dashboard', icon: LayoutDashboard },
        { label: 'Carteira', href: '/assessor/carteira', icon: PieChart },
        { label: 'Produtos', href: '/products', icon: Briefcase },
      ];

    case 'investor':
      return [
        { label: 'Início', href: '/investor/inicio', icon: Home },
        { label: 'Dashboard', href: '/investor/dashboard', icon: LayoutDashboard },
        { label: 'Carteira', href: '/investor/my-investments', icon: PieChart },
        { label: 'Produtos', href: '/products', icon: Briefcase },
        // TEMP: item Beneficios desativado — reativar junto com a rota em App.tsx
        // { label: 'Benefícios', href: '/investor/beneficios', icon: Award },
      ];

    case 'incorporador':
      return [
        { label: 'Dashboard', href: '/incorporador/dashboard', icon: LayoutDashboard },
        { label: 'Diligencias', href: '/incorporador/diligencias', icon: Briefcase },
        { label: 'Incorporacoes', href: '/incorporador/incorporacoes', icon: TrendingUp },
      ];

    default:
      return [
        { label: 'Dashboard', href: '/investor/dashboard', icon: LayoutDashboard },
        { label: 'Carteira', href: '/investor/my-investments', icon: PieChart },
        { label: 'Produtos', href: '/products', icon: Briefcase },
      ];
  }
};

const getProfilePath = (role: BottomNavRole, basePath?: string): string => {
  switch (role) {
    case 'admin':
      return '/admin/profile';
    case 'partner':
      return `${basePath}/profile`;
    case 'assessor':
      return '/assessor/profile';
    case 'incorporador':
      return '/incorporador/profile';
    case 'investor':
    default:
      return '/investor/profile';
  }
};

const getSupportPath = (role: BottomNavRole, basePath?: string): string => {
  switch (role) {
    case 'admin':
      return '/admin/suporte';
    case 'partner':
      return `${basePath}/suporte`;
    case 'assessor':
      return '/assessor/suporte';
    case 'incorporador':
      return '/incorporador/suporte';
    case 'investor':
    default:
      return '/investor/suporte';
  }
};

// =============================================================================
// SUBCOMPONENTS
// =============================================================================

const NavItemButton: React.FC<{
  item: NavItem;
  isActive: boolean;
}> = ({ item, isActive }) => {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.href}
      className="flex flex-1 flex-col items-center justify-center gap-1 py-2 group min-w-0"
    >
      <div
        className={cn(
          'relative p-1.5 rounded-xl transition-all duration-200',
          isActive
            ? 'bg-primary/10'
            : 'group-hover:bg-muted group-active:scale-95'
        )}
      >
        <Icon
          className={cn(
            'h-5 w-5 transition-colors',
            isActive
              ? 'text-primary'
              : 'text-muted-foreground group-hover:text-foreground'
          )}
        />
      </div>
      <span
        className={cn(
          'text-[10px] font-medium tracking-wide transition-colors truncate max-w-full px-1',
          isActive
            ? 'text-primary font-semibold'
            : 'text-muted-foreground group-hover:text-foreground'
        )}
      >
        {item.label}
      </span>
    </NavLink>
  );
};

// Botão + central para Admin
const AdminPlusButton: React.FC<{
  isOpen: boolean;
  onToggle: () => void;
}> = ({ isOpen, onToggle }) => {
  return (
    <button
      onClick={onToggle}
      className={cn(
        'flex flex-col items-center justify-center gap-1 py-2 min-w-0 flex-1',
        'group transition-all duration-200'
      )}
    >
      <div
        className={cn(
          'relative p-2 rounded-full transition-all duration-300',
          isOpen
            ? 'bg-primary rotate-45'
            : 'bg-primary shadow-lg shadow-primary/30'
        )}
      >
        {isOpen ? (
          <X className="h-5 w-5 text-primary-foreground" />
        ) : (
          <Plus className="h-5 w-5 text-primary-foreground" />
        )}
      </div>
      <span className="text-[10px] font-medium text-muted-foreground">
        {isOpen ? 'Fechar' : 'Novo'}
      </span>
    </button>
  );
};

// Submenu do botão + para Admin
const AdminPlusMenu: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const menuItems = [
    { label: 'Aportes Pool', href: '/admin/aportes-trade', icon: Calculator },
    { label: 'Aportes Private', href: '/admin/aportes-Private', icon: Plus },
    { label: 'Distribuições Private', href: '/admin/distribuicoes-Private', icon: CircleDollarSign },
    { label: 'Novo Investimento', href: '/admin/investments?new=true', icon: FolderPlus },
    { label: 'Novo Usuário', href: '/admin/users?new=true', icon: UserPlus },
  ];

  const handleClick = (href: string) => {
    onClose();
    navigate(href);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />

          {/* Menu */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="bg-background border border-border rounded-2xl shadow-xl p-2 min-w-[200px]">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.href}
                    onClick={() => handleClick(item.href)}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-muted transition-colors text-left"
                  >
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Botão de perfil com submenu
const ProfileButton: React.FC<{
  profilePath: string;
  supportPath: string;
  isActive: boolean;
}> = ({ profilePath, supportPath, isActive }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    setIsMenuOpen(false);
    const { error } = await signOut();
    if (!error) {
      navigate('/');
    }
  };

  const handleNavigate = (path: string) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  return (
    <>
      <button
        onClick={() => setIsMenuOpen(true)}
        className="flex flex-1 flex-col items-center justify-center gap-1 py-2 group min-w-0"
      >
        <div className="relative transition-transform duration-200 group-active:scale-95">
          <div
            className={cn(
              'h-7 w-7 rounded-full overflow-hidden border-2 transition-all shadow-sm',
              isActive || isMenuOpen
                ? 'border-primary'
                : 'border-transparent group-hover:border-primary/50'
            )}
          >
            {profile ? (
              <UserAvatar
                avatarUrl={profile.avatar_url}
                firstName={profile.first_name}
                lastName={profile.last_name}
                email={profile.email}
                size="sm"
                className="h-full w-full"
              />
            ) : (
              <div className="h-full w-full bg-muted flex items-center justify-center">
                <span className="text-xs text-muted-foreground">?</span>
              </div>
            )}
          </div>
        </div>
        <span
          className={cn(
            'text-[10px] font-medium tracking-wide transition-colors truncate',
            isActive || isMenuOpen
              ? 'text-primary font-semibold'
              : 'text-muted-foreground group-hover:text-foreground'
          )}
        >
          Perfil
        </span>
      </button>

      {/* Profile Submenu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="fixed bottom-24 right-4 z-50"
            >
              <div className="bg-background border border-border rounded-2xl shadow-xl p-2">
                <div className="flex gap-1">
                  {/* Suporte */}
                  <button
                    onClick={() => handleNavigate(supportPath)}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-muted transition-colors"
                    title="Suporte"
                  >
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <HelpCircle className="h-5 w-5 text-blue-500" />
                    </div>
                    <span className="text-[10px] text-muted-foreground">Suporte</span>
                  </button>

                  {/* Configurações */}
                  <button
                    onClick={() => handleNavigate(profilePath)}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-muted transition-colors"
                    title="Configurações"
                  >
                    <div className="p-2 rounded-lg bg-gray-500/10">
                      <Settings className="h-5 w-5 text-gray-500" />
                    </div>
                    <span className="text-[10px] text-muted-foreground">Config</span>
                  </button>

                  {/* Sair */}
                  <button
                    onClick={handleSignOut}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-muted transition-colors"
                    title="Sair"
                  >
                    <div className="p-2 rounded-lg bg-red-500/10">
                      <LogOut className="h-5 w-5 text-red-500" />
                    </div>
                    <span className="text-[10px] text-muted-foreground">Sair</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const BottomNav: React.FC<BottomNavProps> = ({ role, className }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { profile } = useAuth();
  const authRole = profile?.role;
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);

  // Detectar basePath para partner (suporta /partner, /mfa, /mfb, /escritorio)
  // Em rotas compartilhadas (ex: /products) cai para role do usuário autenticado
  const getPartnerBasePath = () => {
    if (currentPath.startsWith('/mfa')) return '/mfa';
    if (currentPath.startsWith('/mfb')) return '/mfb';
    if (currentPath.startsWith('/escritorio')) return '/escritorio';
    if (currentPath.startsWith('/partner')) return '/partner';
    if (authRole === 'mfa') return '/mfa';
    if (authRole === 'mfb') return '/mfb';
    return '/partner';
  };

  const basePath = role === 'partner' ? getPartnerBasePath() : undefined;
  const navItems = getNavigationItems(role, basePath);
  const profilePath = getProfilePath(role, basePath);
  const supportPath = getSupportPath(role, basePath);

  // Check if a nav item is active
  const isItemActive = (href: string) => {
    return currentPath === href || currentPath.startsWith(href + '/');
  };

  // Check if profile is active
  const isProfileActive = currentPath === profilePath || currentPath.startsWith(profilePath);

  const isAdmin = role === 'admin';

  return (
    <>
      {/* Admin Plus Menu */}
      {isAdmin && (
        <AdminPlusMenu
          isOpen={isPlusMenuOpen}
          onClose={() => setIsPlusMenuOpen(false)}
        />
      )}

      <nav
        className={cn(
          'fixed bottom-0 left-0 w-full z-50 md:hidden',
          className
        )}
      >
        {/* Background layer with blur */}
        <div className="absolute inset-0 bg-background/95 backdrop-blur-xl border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.1)]" />

        {/* Navigation content */}
        <div
          className={cn(
            'relative flex justify-around items-center w-full',
            'pb-[max(8px,env(safe-area-inset-bottom))]',
            'pt-1 px-2'
          )}
        >
          {isAdmin ? (
            <>
              {/* Admin: Dashboard, Investimentos */}
              {navItems.slice(0, 2).map((item) => (
                <NavItemButton
                  key={item.href}
                  item={item}
                  isActive={isItemActive(item.href)}
                />
              ))}

              {/* Admin: Botão + central */}
              <AdminPlusButton
                isOpen={isPlusMenuOpen}
                onToggle={() => setIsPlusMenuOpen(!isPlusMenuOpen)}
              />

              {/* Admin: Usuários */}
              {navItems.slice(2).map((item) => (
                <NavItemButton
                  key={item.href}
                  item={item}
                  isActive={isItemActive(item.href)}
                />
              ))}

              {/* Admin: Perfil */}
              <ProfileButton
                profilePath={profilePath}
                supportPath={supportPath}
                isActive={isProfileActive}
              />
            </>
          ) : (
            <>
              {/* Outros roles: Dashboard, Carteira, Produtos */}
              {navItems.map((item) => (
                <NavItemButton
                  key={item.href}
                  item={item}
                  isActive={isItemActive(item.href)}
                />
              ))}

              {/* Perfil */}
              <ProfileButton
                profilePath={profilePath}
                supportPath={supportPath}
                isActive={isProfileActive}
              />
            </>
          )}
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
